import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  Bot,
  Building2,
  CreditCard,
  KeyRound,
  ShieldCheck,
  UserCircle,
  Wallet,
} from 'lucide-react';
import type { m } from '~/paraglide/messages.js';

type MessageKey = keyof typeof m;

export const CLI_AUTH_SUCCESS_KEY = 'cli_auth_success';

export type PermissionScope = {
  key: string;
  label: MessageKey;
};

export type PermissionGroup = {
  id: string;
  icon: LucideIcon;
  label: MessageKey;
  scopes: PermissionScope[];
};

export const CLI_PERMISSIONS: PermissionGroup[] = [
  {
    id: 'identity',
    icon: UserCircle,
    label: 'cli_auth.permissions.identity',
    scopes: [
      {
        key: 'identity.read.self',
        label: 'cli_auth.permissions.identity_read',
      },
      {
        key: 'identity.write.self',
        label: 'cli_auth.permissions.identity_write',
      },
      {
        key: 'identity.update.self',
        label: 'cli_auth.permissions.identity_update',
      },
      { key: 'alias.read.self', label: 'cli_auth.permissions.alias_read' },
      { key: 'alias.write.self', label: 'cli_auth.permissions.alias_write' },
    ],
  },
  {
    id: 'orgs',
    icon: Building2,
    label: 'cli_auth.permissions.orgs',
    scopes: [
      { key: 'orgs.create', label: 'cli_auth.permissions.orgs_create' },
      { key: 'orgs.read.self', label: 'cli_auth.permissions.orgs_read' },
      { key: 'orgs.write.self', label: 'cli_auth.permissions.orgs_write' },
      { key: 'invite.accept', label: 'cli_auth.permissions.invite_accept' },
    ],
  },
  {
    id: 'accounts',
    icon: Wallet,
    label: 'cli_auth.permissions.accounts',
    scopes: [
      {
        key: 'accounts.read.self',
        label: 'cli_auth.permissions.accounts_read',
      },
      {
        key: 'accounts.balance',
        label: 'cli_auth.permissions.accounts_balance',
      },
      {
        key: 'accounts.transfer.self',
        label: 'cli_auth.permissions.accounts_transfer',
      },
      {
        key: 'accounts.list_transactions',
        label: 'cli_auth.permissions.accounts_transactions',
      },
      {
        key: 'accounts.tokenize.self',
        label: 'cli_auth.permissions.accounts_tokenize',
      },
      { key: 'accounts.batch', label: 'cli_auth.permissions.accounts_batch' },
      { key: 'mediums.read', label: 'cli_auth.permissions.mediums_read' },
      {
        key: 'mediums.write.self',
        label: 'cli_auth.permissions.mediums_write',
      },
    ],
  },
  {
    id: 'payments',
    icon: CreditCard,
    label: 'cli_auth.permissions.payments',
    scopes: [
      {
        key: 'payments.write.self',
        label: 'cli_auth.permissions.payments_create',
      },
      {
        key: 'payments.write',
        label: 'cli_auth.permissions.payments_write_all',
      },
      {
        key: 'payments.read.self',
        label: 'cli_auth.permissions.payments_read',
      },
      { key: 'payments.read', label: 'cli_auth.permissions.payments_read_all' },
    ],
  },
  {
    id: 'swaps',
    icon: ArrowLeftRight,
    label: 'cli_auth.permissions.swaps',
    scopes: [
      { key: 'swap.take', label: 'cli_auth.permissions.swap_take' },
      { key: 'swap.find_rate', label: 'cli_auth.permissions.swap_find_rate' },
      { key: 'swap.read', label: 'cli_auth.permissions.swap_read' },
      { key: 'swap.write', label: 'cli_auth.permissions.swap_write' },
    ],
  },
  {
    id: 'compliance',
    icon: ShieldCheck,
    label: 'cli_auth.permissions.compliance',
    scopes: [
      { key: 'compliance.read', label: 'cli_auth.permissions.compliance_read' },
      {
        key: 'compliance.write',
        label: 'cli_auth.permissions.compliance_write',
      },
      {
        key: 'compliance.start_verification',
        label: 'cli_auth.permissions.compliance_verify',
      },
    ],
  },
  {
    id: 'agent',
    icon: Bot,
    label: 'cli_auth.permissions.agent',
    scopes: [
      { key: 'agent.read', label: 'cli_auth.permissions.agent_read' },
      { key: 'agent.write', label: 'cli_auth.permissions.agent_write' },
      { key: 'chat.read', label: 'cli_auth.permissions.chat_read' },
      { key: 'chat.write', label: 'cli_auth.permissions.chat_write' },
      { key: 'file.read', label: 'cli_auth.permissions.file_read' },
      { key: 'file.write', label: 'cli_auth.permissions.file_write' },
    ],
  },
  {
    id: 'security',
    icon: KeyRound,
    label: 'cli_auth.permissions.security',
    scopes: [
      { key: 'signer.get.self', label: 'cli_auth.permissions.signer_get' },
      {
        key: 'signer.register.self',
        label: 'cli_auth.permissions.signer_register',
      },
      { key: 'signer.list.self', label: 'cli_auth.permissions.signer_list' },
      { key: 'api-key.create', label: 'cli_auth.permissions.apikey_create' },
      { key: 'api-key.list', label: 'cli_auth.permissions.apikey_list' },
      {
        key: 'graph.template.generate',
        label: 'cli_auth.permissions.graph_generate',
      },
    ],
  },
];
